/* eslint-disable react/forbid-prop-types */
import { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Drawer, Box, Stack, Typography, IconButton, Chip, Skeleton, alpha,
    Button, Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { common, grey } from 'src/theme/palette';
import ConsentFormRenderer from 'src/pages/public/components/ConsentFormRenderer';
import {
    transformFormData,
    extractFormMetadata,
} from 'src/pages/superAdmin/ConsentV2/components/ConsentFormPreview';

// Sensible default while the ResizeObserver measures the real stage width.
// Prevents the brief frame at scale=1 (overflowing drawer) on initial open.
// 700px matches the typical 760-px drawer paper minus padding.
const INITIAL_STAGE_WIDTH = 700;

/* ──────────────────────────────────────────────────────────────────────────
   FormQuickPreviewDrawer
   ──────────────────────
   Practice-side preview of a PracticeForm rendered through the same
   ``ConsentFormRenderer`` the patient sees. Inspired by Chrome DevTools'
   responsive mode: the user picks a device (Desktop / Tablet / Mobile) and
   the renderer is laid out at the chosen viewport width, then visually
   scaled to fit inside the drawer.

   Why scale instead of just resizing the container?
   The renderer uses a 12-column grid where ``columnSpan`` is set per field
   in ``metadata.style.columnSpan``. A title field at columnSpan=2 + first
   name at columnSpan=5 + last name at columnSpan=5 reflows nicely at
   desktop/tablet widths, but at 360px the columns get squished into a
   single readable line. Rendering at native widths and scaling to fit
   keeps the grid faithful while still letting the practice user see all
   three sizes from a fixed-width drawer.

   Colour system:
   - Drawer body uses a subtle slate gradient so the device frames feel
     like they're sitting on a designer's desk surface.
   - Each frame gets its own bezel colour (graphite for desktop, slate for
     tablet, jet for mobile).
   - Header retains the form's type-color strip for continuity with
     FormSelector / SelectedFormsList cards.
   ────────────────────────────────────────────────────────────────────────── */

// Device profiles. ``frameInsets`` are CSS padding values inside the device
// chrome where the form viewport sits. Heights are caps; the renderer is
// allowed to overflow vertically and the inner viewport scrolls.
const DEVICES = {
    desktop: {
        label: 'Desktop',
        icon: 'solar:monitor-bold-duotone',
        width: 1280,
        height: 760,
        chromeHeight: 36, // browser tab bar
        bezelColor: '#1F2937',
        bezelRadius: 14,
    },
    tablet: {
        label: 'Tablet',
        icon: 'solar:tablet-bold-duotone',
        width: 820,
        height: 1024,
        chromeHeight: 0,
        bezelColor: '#37404D',
        bezelRadius: 36,
    },
    mobile: {
        label: 'Mobile',
        icon: 'solar:smartphone-bold-duotone',
        width: 390,
        height: 700,
        chromeHeight: 0,
        bezelColor: '#0F1419',
        bezelRadius: 44,
    },
};

// Tablet matches the typical patient experience for consent forms
// (waiting-room iPad, in-clinic kiosk, or laptop) better than desktop, so
// the drawer opens on tablet by default and the user can toggle out.
const DEFAULT_DEVICE = 'tablet';

// ── Component ──────────────────────────────────────────────────────────────

const FormQuickPreviewDrawer = ({
    open,
    onClose,
    practiceId,
    form,
    typeColor,
    typeLabel,
    isSelected,
    onAdd,
    onRemove,
}) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [device, setDevice] = useState(DEFAULT_DEVICE);

    // Available pixel width inside the drawer for the device frame. Measured
    // dynamically so the scale calculation tracks the real viewport. Seeded
    // with a sensible default so the first paint isn't at scale=1 — the
    // Drawer's slide animation means the stage element has zero width on
    // mount, and a 1280-px desktop preview at scale=1 overflows the drawer.
    const stageRef = useRef(null);
    const [stageWidth, setStageWidth] = useState(INITIAL_STAGE_WIDTH);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const el = stageRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width || el.clientWidth || 0;
            // Ignore zero-width measurements that can come from the
            // drawer's enter animation; keep the previous value until a
            // real layout pass settles.
            if (w > 0) setStageWidth(w);
        });
        ro.observe(el);
        const measured = el.clientWidth || 0;
        if (measured > 0) setStageWidth(measured);
        return () => ro.disconnect();
    }, [open]);

    // Fetch the full form (no ?fields= projection — the renderer reads
    // metadata.frontend_type, metadata.placeholder, options_full, long_text,
    // choices, etc. Projecting any subset risks dropping fields the
    // renderer needs).
    useEffect(() => {
        if (!open || !form?.id || !practiceId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setDetail(null);
        callApi(`/api/v1/practices/${practiceId}/forms/${form.id}/`)
            .then((res) => {
                if (cancelled) return;
                if (res.status === 200) {
                    setDetail(res.data);
                } else {
                    setError(res.data?.detail || 'Failed to load form');
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || 'Failed to load form');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, form?.id, practiceId]);

    // Reset device toggle when the drawer opens for a fresh form.
    useEffect(() => {
        if (open) setDevice(DEFAULT_DEVICE);
    }, [open, form?.id]);

    const transformed = useMemo(
        () => (detail ? transformFormData(detail) : null),
        [detail]
    );
    const formMeta = useMemo(
        () => (detail ? extractFormMetadata(detail) : null),
        [detail]
    );
    // Stable references so downstream useMemos don't re-run on every render
    // (transformed could be null → fallback objects must be referentially
    // stable too).
    const sections = useMemo(
        () => transformed?.sections || [],
        [transformed]
    );
    const conditionalLogics = useMemo(
        () => transformed?.conditional_logics || [],
        [transformed]
    );

    // ── Step navigation (advanced/step forms) ─────────────────────────────
    // All hooks must run unconditionally — keep them above any early
    // ``return`` so the hook order is stable across renders. The form-null
    // guard moves below.
    const stepGroups = useMemo(() => {
        const raw = formMeta?.stepGroups || [];
        if (!raw.length || !sections.length) return raw;
        const frontendToApiId = {};
        sections.forEach((s) => {
            if (s.frontend_id) frontendToApiId[s.frontend_id] = s.id;
        });
        if (Object.keys(frontendToApiId).length === 0) return raw;
        return raw.map((group) => ({
            ...group,
            section_ids: (group.section_ids || []).map((sid) => frontendToApiId[sid] || sid),
        }));
    }, [formMeta, sections]);

    const isStepForm = (formMeta?.formMode === 'advanced') && stepGroups.length > 0;
    const totalSteps = stepGroups.length;
    const [currentStep, setCurrentStep] = useState(0);
    useEffect(() => { setCurrentStep(0); }, [form?.id, isStepForm]);

    const handleStepBack = useCallback(() => {
        setCurrentStep((prev) => Math.max(0, prev - 1));
    }, []);
    const handleStepNext = useCallback(() => {
        setCurrentStep((prev) => Math.min(totalSteps - 1, prev + 1));
    }, [totalSteps]);

    if (!form) return null;

    const deviceCfg = DEVICES[device] || DEVICES[DEFAULT_DEVICE];
    // Cap at 1.0 so mobile preview (390px) doesn't get blown up beyond
    // native; clamp at a tiny minimum so a runtime measurement glitch can
    // never produce a 0-width frame.
    const horizontalPadding = 48;
    const usableStageWidth = Math.max(280, stageWidth - horizontalPadding);
    const scale = Math.min(1, usableStageWidth / deviceCfg.width);

    const currentStepGroup = stepGroups[currentStep] || {};
    const currentSectionIds = isStepForm ? (currentStepGroup.section_ids || []) : null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={(theme) => ({ zIndex: theme.zIndex.modal + 10 })}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 640, md: 760, lg: 920 },
                    maxWidth: '100%',
                    bgcolor: 'transparent',
                    borderTopLeftRadius: { sm: '20px' },
                    borderBottomLeftRadius: { sm: '20px' },
                    overflow: 'hidden',
                    // Slate gradient — feels like a designer's desk surface
                    backgroundImage: `linear-gradient(180deg, ${alpha('#212B36', 0.04)} 0%, ${alpha('#637381', 0.06)} 100%), linear-gradient(180deg, #F4F6F8 0%, #E9ECEF 100%)`,
                },
            }}
            BackdropProps={{
                sx: { bgcolor: alpha('#161C24', 0.5) },
            }}
        >
            <Stack sx={{ height: '100%' }}>
                {/* ── Header ─────────────────────────────────────────── */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: alpha(grey[500], 0.16),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: '#fff',
                        zIndex: 1,
                    }}
                >
                    {/* Type-colour strip */}
                    <Box
                        sx={{
                            width: 4,
                            alignSelf: 'stretch',
                            borderRadius: 1,
                            bgcolor: typeColor || '#637381',
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: 16, fontWeight: 700, color: '#212B36',
                                lineHeight: 1.3, mb: 0.5,
                            }}
                            noWrap
                        >
                            {form.name || 'Form preview'}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Chip
                                size="small"
                                label={typeLabel || form.form_type}
                                sx={{
                                    height: 20, fontSize: 10.5, fontWeight: 700,
                                    bgcolor: alpha(typeColor || '#637381', 0.12),
                                    color: typeColor || '#637381',
                                    textTransform: 'uppercase', letterSpacing: '0.3px',
                                }}
                            />
                            {detail && (
                                <Typography variant="caption" sx={{ fontSize: 11, color: grey[500] }}>
                                    {sections.length} section{sections.length !== 1 ? 's' : ''}
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    {/* Device toggle — segmented control */}
                    <ToggleButtonGroup
                        value={device}
                        exclusive
                        onChange={(_, next) => { if (next) setDevice(next); }}
                        size="small"
                        sx={{
                            bgcolor: alpha(grey[500], 0.08),
                            borderRadius: '10px',
                            p: 0.25,
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 0,
                                mx: 0.25,
                                borderRadius: '8px !important',
                            },
                            '& .MuiToggleButton-root': {
                                px: 1.25, py: 0.5,
                                color: grey[600],
                                textTransform: 'none',
                                fontSize: 11.5, fontWeight: 600,
                                '&.Mui-selected': {
                                    bgcolor: '#fff',
                                    color: '#212B36',
                                    boxShadow: `0 1px 2px ${alpha('#000', 0.08)}`,
                                    '&:hover': { bgcolor: '#fff' },
                                },
                            },
                        }}
                    >
                        {Object.entries(DEVICES).map(([key, cfg]) => (
                            <ToggleButton key={key} value={key} aria-label={cfg.label}>
                                <Iconify icon={cfg.icon} width={15} sx={{ mr: 0.5 }} />
                                {cfg.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <IconButton size="small" onClick={onClose} aria-label="Close preview">
                        <Iconify icon="eva:close-fill" width={20} />
                    </IconButton>
                </Box>

                {/* ── Stage ──────────────────────────────────────────── */}
                <Box
                    ref={stageRef}
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        py: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                    }}
                >
                    {loading && (
                        <Stack spacing={1.5} sx={{ width: 360, p: 2 }}>
                            <Skeleton variant="rounded" height={28} />
                            <Skeleton variant="rounded" height={56} />
                            <Skeleton variant="rounded" height={56} />
                            <Skeleton variant="rounded" height={120} />
                        </Stack>
                    )}

                    {!loading && error && (
                        <Box
                            sx={{
                                p: 2, borderRadius: '10px',
                                bgcolor: alpha('#FF5630', 0.08),
                                color: '#B71D18', fontSize: 12.5,
                                maxWidth: 360,
                            }}
                        >
                            {error}
                        </Box>
                    )}

                    {!loading && !error && transformed && (
                        <DeviceFrame
                            device={device}
                            scale={scale}
                            sections={sections}
                            conditionalLogics={conditionalLogics}
                            formMeta={formMeta}
                            sectionIds={currentSectionIds}
                            stepInfo={isStepForm ? {
                                current: currentStep,
                                total: totalSteps,
                                stepName: currentStepGroup.name,
                                onBack: handleStepBack,
                                onNext: handleStepNext,
                            } : null}
                        />
                    )}
                </Box>

                {/* ── Footer ─────────────────────────────────────────── */}
                <Box
                    sx={{
                        px: 2.5, py: 1.75,
                        borderTop: '1px solid',
                        borderColor: alpha(grey[500], 0.16),
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        bgcolor: '#fff',
                        zIndex: 1,
                    }}
                >
                    <Tooltip
                        title="This is a read-only preview. Real patients can fill it in."
                        arrow
                        placement="top"
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', color: grey[600], gap: 0.5 }}>
                            <Iconify icon="solar:eye-bold-duotone" width={16} />
                            <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
                                Read-only
                            </Typography>
                        </Box>
                    </Tooltip>
                    {/* Native viewport size pill — useful for designers */}
                    <Chip
                        size="small"
                        label={`${deviceCfg.width} × ${deviceCfg.height}px`}
                        variant="outlined"
                        sx={{
                            height: 22, fontSize: 10.5, fontWeight: 600,
                            color: grey[600], borderColor: alpha(grey[500], 0.2),
                            fontFamily: 'monospace',
                        }}
                    />
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={onClose}
                        sx={{ ml: 'auto', textTransform: 'none', borderRadius: '10px' }}
                    >
                        Close
                    </Button>
                    {isSelected ? (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Iconify icon="eva:minus-circle-outline" width={18} />}
                            onClick={() => { if (onRemove) onRemove(form); onClose(); }}
                            sx={{ textTransform: 'none', borderRadius: '10px' }}
                        >
                            Remove
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<Iconify icon="eva:plus-circle-outline" width={18} />}
                            onClick={() => { if (onAdd) onAdd(form); onClose(); }}
                            sx={{
                                textTransform: 'none', borderRadius: '10px',
                                bgcolor: common.mintyZest,
                                '&:hover': { bgcolor: alpha(common.mintyZest, 0.85) },
                            }}
                        >
                            Add to selection
                        </Button>
                    )}
                </Box>
            </Stack>
        </Drawer>
    );
};

// ── DeviceFrame ────────────────────────────────────────────────────────────
// Renders the form inside a device-specific chrome (browser / tablet /
// phone) at the device's native viewport width, then visually scales the
// whole frame down to fit inside the drawer's measured stage width.
const DeviceFrame = ({ device, scale, sections, conditionalLogics, formMeta, sectionIds, stepInfo }) => {
    const cfg = DEVICES[device] || DEVICES[DEFAULT_DEVICE];
    const innerWidth = cfg.width;
    const innerHeight = cfg.height;

    // Mobile responsive override — collapse multi-column fields to a single
    // column. The renderer uses ``field.columnSpan`` (out of 12) authored
    // into the form, so a desktop-friendly Title|First|Last 2/5/5 split
    // stays as three squished columns at 390px native. Modern responsive
    // forms collapse to one column on mobile; mirror that here so the
    // preview reads cleanly. The PC/tablet previews are untouched.
    const stageSections = useMemo(() => {
        if (device !== 'mobile') return sections;
        return sections.map((s) => ({
            ...s,
            fields: (s.fields || []).map((f) => ({ ...f, columnSpan: 12 })),
        }));
    }, [sections, device]);

    const stage = (
        <FormStage
            sections={stageSections}
            conditionalLogics={conditionalLogics}
            formMeta={formMeta}
            sectionIds={sectionIds}
            stepInfo={stepInfo}
            device={device}
        />
    );

    // The wrapper occupies the SCALED bounding box so the parent flexbox
    // and scroll container size correctly. The actual frame is rendered at
    // native size and transformed.
    return (
        <Box
            sx={{
                width: innerWidth * scale,
                height: innerHeight * scale,
                position: 'relative',
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: innerWidth,
                    height: innerHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                }}
            >
                {device === 'desktop' && <DesktopChrome cfg={cfg}>{stage}</DesktopChrome>}
                {device === 'tablet' && <TabletChrome cfg={cfg}>{stage}</TabletChrome>}
                {device === 'mobile' && <MobileChrome cfg={cfg}>{stage}</MobileChrome>}
            </Box>
        </Box>
    );
};

DeviceFrame.propTypes = {
    device: PropTypes.oneOf(Object.keys(DEVICES)).isRequired,
    scale: PropTypes.number.isRequired,
    sections: PropTypes.array.isRequired,
    conditionalLogics: PropTypes.array,
    formMeta: PropTypes.object,
    sectionIds: PropTypes.array,
    stepInfo: PropTypes.object,
};
DeviceFrame.defaultProps = { conditionalLogics: [], formMeta: null, sectionIds: null, stepInfo: null };

// We pass `device` through DeviceFrame → FormStage so the stage padding
// can match the device's screen width without re-deriving it.

// ── Chrome variants ────────────────────────────────────────────────────────

// Browser-window chrome. Three traffic-light dots + URL pill. Inner area is
// a plain white scrollable canvas — the renderer paints into it.
const DesktopChrome = ({ cfg, children }) => (
    <Box
        sx={{
            width: cfg.width, height: cfg.height,
            borderRadius: `${cfg.bezelRadius}px`,
            border: `1px solid ${alpha('#000', 0.08)}`,
            bgcolor: '#fff',
            boxShadow: '0 30px 80px -30px rgba(15, 23, 42, 0.45), 0 12px 30px -12px rgba(15, 23, 42, 0.2)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
        }}
    >
        <Box
            sx={{
                height: cfg.chromeHeight,
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2,
                bgcolor: '#F4F6F8',
                borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            }}
        >
            <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Dot color="#FF5F57" />
                <Dot color="#FEBC2E" />
                <Dot color="#28C840" />
            </Box>
            <Box
                sx={{
                    ml: 2, flex: 1, height: 22, borderRadius: '6px',
                    bgcolor: '#fff',
                    border: `1px solid ${alpha('#000', 0.06)}`,
                    display: 'flex', alignItems: 'center', px: 1.25,
                    fontSize: 11, color: grey[500], fontFamily: 'monospace',
                }}
            >
                <Iconify icon="eva:lock-fill" width={11} sx={{ mr: 0.75, color: '#00AB55' }} />
                forms.dental-sem.co.uk/f/preview-link
            </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#FAFBFC' }}>
            {children}
        </Box>
    </Box>
);

DesktopChrome.propTypes = { cfg: PropTypes.object.isRequired, children: PropTypes.node.isRequired };

// iPad-style frame. Slim bezel + bottom home indicator pill.
const TabletChrome = ({ cfg, children }) => (
    <Box
        sx={{
            width: cfg.width, height: cfg.height,
            borderRadius: `${cfg.bezelRadius}px`,
            bgcolor: cfg.bezelColor,
            padding: '20px 14px',
            boxShadow: '0 30px 80px -30px rgba(15, 23, 42, 0.5)',
            position: 'relative',
        }}
    >
        <Box
            sx={{
                width: '100%', height: '100%',
                bgcolor: '#fff',
                borderRadius: '18px',
                overflow: 'auto',
            }}
        >
            {children}
        </Box>
        {/* Home indicator (subtle at the bottom of the screen area) */}
        <Box
            sx={{
                position: 'absolute',
                bottom: 6, left: '50%', transform: 'translateX(-50%)',
                width: 110, height: 4,
                borderRadius: 2,
                bgcolor: alpha('#fff', 0.4),
            }}
        />
    </Box>
);

TabletChrome.propTypes = { cfg: PropTypes.object.isRequired, children: PropTypes.node.isRequired };

// iPhone-style frame with a Dynamic-Island-ish notch + tactile bezel.
const MobileChrome = ({ cfg, children }) => (
    <Box
        sx={{
            width: cfg.width, height: cfg.height,
            borderRadius: `${cfg.bezelRadius}px`,
            bgcolor: cfg.bezelColor,
            padding: '12px 8px 18px',
            boxShadow: '0 30px 80px -30px rgba(15, 23, 42, 0.55), inset 0 0 0 1.5px rgba(255,255,255,0.06)',
            position: 'relative',
        }}
    >
        {/* Dynamic-Island notch */}
        <Box
            sx={{
                position: 'absolute',
                top: 22, left: '50%', transform: 'translateX(-50%)',
                width: 100, height: 26,
                borderRadius: 13,
                bgcolor: '#000',
                zIndex: 2,
            }}
        />
        <Box
            sx={{
                width: '100%', height: '100%',
                bgcolor: '#fff',
                borderRadius: '32px',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
            }}
        >
            {/* Status bar spacer below the notch */}
            <Box sx={{ height: 44, flexShrink: 0 }} />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {children}
            </Box>
        </Box>
        {/* Home indicator */}
        <Box
            sx={{
                position: 'absolute',
                bottom: 7, left: '50%', transform: 'translateX(-50%)',
                width: 130, height: 4,
                borderRadius: 2,
                bgcolor: alpha('#fff', 0.6),
                zIndex: 2,
            }}
        />
    </Box>
);

MobileChrome.propTypes = { cfg: PropTypes.object.isRequired, children: PropTypes.node.isRequired };

const Dot = ({ color }) => (
    <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: color }} />
);
Dot.propTypes = { color: PropTypes.string.isRequired };

// ── FormStage — renders the actual form inside whichever chrome ────────────
// Pads sensibly per device. When the form is authored as a multi-step
// (advanced) form, only the current step's sections render — driven by the
// renderer's ``sectionIds`` filter — and a small step navigator sits at the
// bottom so the practice user can walk the form the way the patient will.
// Per-device padding. Mobile uses the tightest gutters because 390px native
// is unforgiving — and since we already collapse every field to
// columnSpan=12 there, a 16px gutter is plenty. Tablet and desktop get
// progressively more generous padding because their grids breathe at wider
// widths.
const STAGE_PADDING_X = { desktop: 8, tablet: 4, mobile: 2 };
const STAGE_PADDING_Y = { desktop: 5, tablet: 3, mobile: 2 };

const FormStage = ({ sections, conditionalLogics, formMeta, sectionIds, stepInfo, device }) => (
    <Box
        sx={{
            display: 'flex', flexDirection: 'column',
            minHeight: '100%',
            px: STAGE_PADDING_X[device] ?? STAGE_PADDING_X.desktop,
            py: STAGE_PADDING_Y[device] ?? STAGE_PADDING_Y.desktop,
        }}
    >
        {formMeta?.userDescription && (
            <Typography
                sx={{
                    fontSize: 14, color: '#454F5B',
                    mb: 3, whiteSpace: 'pre-wrap', maxWidth: 720,
                }}
            >
                {formMeta.userDescription}
            </Typography>
        )}

        {/* Step header — only when this is a step form */}
        {stepInfo && (
            <Stack
                direction="row" alignItems="center" spacing={1}
                sx={{
                    mb: 3, px: 2, py: 1.25,
                    bgcolor: alpha(common.mintyZest, 0.06),
                    border: `1px solid ${alpha(common.mintyZest, 0.18)}`,
                    borderRadius: '10px',
                }}
            >
                <Iconify
                    icon="solar:list-arrow-down-bold-duotone"
                    width={18}
                    sx={{ color: common.mintyZest }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: grey[600], textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Step {stepInfo.current + 1} of {stepInfo.total}
                    </Typography>
                    {stepInfo.stepName && (
                        <Typography sx={{ fontSize: 13, color: '#212B36', fontWeight: 600 }} noWrap>
                            {stepInfo.stepName}
                        </Typography>
                    )}
                </Box>
            </Stack>
        )}

        {/* Form body — fills remaining height so step nav can stick to the bottom */}
        <Box sx={{ flex: 1 }}>
            {sections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Iconify icon="solar:document-text-bold-duotone" width={42} sx={{ color: '#C4CDD5', mb: 1 }} />
                    <Typography sx={{ fontSize: 13, color: grey[500] }}>
                        This form has no sections yet.
                    </Typography>
                </Box>
            ) : (
                <ConsentFormRenderer
                    sections={sections}
                    fieldValues={{}}
                    onChange={() => {}}
                    errors={{}}
                    conditionalLogics={conditionalLogics}
                    readOnly
                    skipBrandSection
                    theme={formMeta?.theme || null}
                    sectionIds={sectionIds || undefined}
                />
            )}
        </Box>

        {/* Step footer — Previous / Next buttons with the same colour rule
            ConsentFormPreview uses, so the preview matches the live form. */}
        {stepInfo && (
            <Stack
                direction="row" alignItems="center" spacing={1.5}
                sx={{
                    mt: 4, pt: 2,
                    borderTop: `1px solid ${alpha(grey[500], 0.16)}`,
                }}
            >
                <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
                    onClick={stepInfo.onBack}
                    disabled={stepInfo.current === 0}
                    sx={{ textTransform: 'none', borderRadius: '10px' }}
                >
                    Previous
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="contained"
                    size="medium"
                    endIcon={<Iconify icon="eva:arrow-forward-fill" width={16} />}
                    onClick={stepInfo.onNext}
                    disabled={stepInfo.current >= stepInfo.total - 1}
                    sx={{
                        textTransform: 'none', borderRadius: '10px',
                        bgcolor: common.mintyZest,
                        '&:hover': { bgcolor: alpha(common.mintyZest, 0.85) },
                    }}
                >
                    {stepInfo.current >= stepInfo.total - 1 ? 'Submit' : 'Continue'}
                </Button>
            </Stack>
        )}
    </Box>
);

FormStage.propTypes = {
    sections: PropTypes.array.isRequired,
    conditionalLogics: PropTypes.array,
    formMeta: PropTypes.object,
    sectionIds: PropTypes.array,
    stepInfo: PropTypes.object,
    device: PropTypes.oneOf(['desktop', 'tablet', 'mobile']),
};
FormStage.defaultProps = {
    conditionalLogics: [], formMeta: null, sectionIds: null, stepInfo: null, device: 'desktop',
};

// ── PropTypes ──────────────────────────────────────────────────────────────

FormQuickPreviewDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    form: PropTypes.object,
    typeColor: PropTypes.string,
    typeLabel: PropTypes.string,
    isSelected: PropTypes.bool,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
};

FormQuickPreviewDrawer.defaultProps = {
    form: null,
    typeColor: '#637381',
    typeLabel: '',
    isSelected: false,
    onAdd: undefined,
    onRemove: undefined,
};

export default FormQuickPreviewDrawer;
